import { Injectable, WritableSignal, signal } from '@angular/core';

import { ModalController } from '@ionic/angular';

import { AppSettingsService } from '@core/services/app-settings/app-settings.service';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { UserDataService } from '@core/services/user-data/user-data.service';

import { BaseProfileCommand } from '@shared/commands/base-profile.command';
import { IProfileCommandContext } from '@shared/models/profile-command.model';
import { CommandAvailabilityService } from '@shared/services/command-availability.service';

@Injectable()
export class ShowHiddenProductsCommand extends BaseProfileCommand {
  readonly type = 'showHiddenProducts';
  readonly title = 'Скрытые счета';
  readonly subtitle?: WritableSignal<string> = undefined;
  readonly icon = 'eye-slash';
  readonly checkbox?: 'unchecked' | 'checked' | 'disabled' | null = null;
  readonly hidden = false;
  readonly group = 'display';
  readonly order = 2;
  // Обновлено согласно задаче #26325: единая настройка для всего функционала скрытия счетов
  readonly settingsNames?: string[] = ['REQUEST/PRODUCT_RESTRICTION'];
  readonly checkboxChecked = signal(false);

  constructor(
    private readonly modalController: ModalController,
    private readonly userDataService: UserDataService,
    private navigationService: NavigationService,
    commandAvailabilityService?: CommandAvailabilityService,
    appSettingsService?: AppSettingsService
  ) {
    super(commandAvailabilityService, appSettingsService);

    this.initializeCheckboxState();
  }

  async execute(_context: IProfileCommandContext): Promise<void> {
    // Динамический импорт компонента для избежания циклических зависимостей
    const { ShowHiddenProductsModalComponent } = await import(
      '../_modals/show-hidden-products/show-hidden-products-modal.component'
    );

    const modal = await this.modalController.create({
      component: ShowHiddenProductsModalComponent,
      handle: false,
      cssClass: 'custom',
      initialBreakpoint: 1,
      componentProps: {
        staticHeight: true,
        modal: this.modalController,
        openChangePassword: () => this.openChangeHiddenPasswordModal()
      }
    });

    await modal.present();
  }

  /**
   * Переключает состояние чекбокса (вызывается при клике на чекбокс)
   */
  public async toggle(): Promise<void> {
    await this.execute({ data: {} });
  }

  private async openChangeHiddenPasswordModal(): Promise<void> {
    this.navigationService.push('other/profile/account-restrictions/change-hidden-password');
  }

  /**
   * Инициализирует состояние чекбокса из настроек
   */
  private async initializeCheckboxState(): Promise<void> {
    const hasRestrictPasswd = this.toBoolean(this.userDataService.userData.hasRestrictPasswd);

    this.updateCheckboxState(hasRestrictPasswd);
  }

  private toBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      const v = value.trim().toLowerCase();

      return v === 'true' || v === '1';
    }
    if (typeof value === 'number') {
      return value === 1;
    }

    return false;
  }
}
